import { useEffect, useRef } from 'react'
import { presenceService } from '../services/api'
import { HEARTBEAT_INTERVAL_MS, HEARTBEAT_MIN_GAP_MS, nextHeartbeatDelay } from '../utils/presencePolicy'

const CHANNEL = 'camit-presence'

export function useActivityHeartbeat(enabled) {
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false
    let timer = 0
    let inFlight = false
    let attempt = 0
    let lastBeat = 0
    let channel
    try {
      channel = new BroadcastChannel(CHANNEL)
    } catch {
      channel = null
    }

    const schedule = (delay) => {
      if (cancelled || delay == null) return
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        beat()
      }, delay)
    }

    const beat = () => {
      if (cancelled || !enabledRef.current) return
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        schedule(HEARTBEAT_INTERVAL_MS)
        return
      }
      if (inFlight) return
      const now = Date.now()
      if (now - lastBeat < HEARTBEAT_MIN_GAP_MS) {
        schedule(HEARTBEAT_MIN_GAP_MS - (now - lastBeat))
        return
      }
      inFlight = true
      presenceService.heartbeat()
        .then((payload) => {
          if (payload?.recorded === false) return
          attempt = 0
          lastBeat = Date.now()
          try {
            channel?.postMessage({ at: lastBeat })
          } catch {
            /* ignore */
          }
          schedule(HEARTBEAT_INTERVAL_MS)
        })
        .catch((error) => {
          const status = error.response?.status
          const delay = nextHeartbeatDelay(status, attempt)
          lastBeat = Date.now()
          attempt += 1
          schedule(delay)
        })
        .finally(() => {
          inFlight = false
        })
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') beat()
    }
    const onPeer = () => {
      lastBeat = Date.now()
    }

    beat()
    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisible)
    channel?.addEventListener('message', onPeer)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
      try {
        channel?.removeEventListener('message', onPeer)
        channel?.close()
      } catch {
        /* ignore */
      }
    }
  }, [enabled])
}
